interface YouTubePlayer {
    destroy(): void;
}

interface YouTubePlayerApi {
    Player: new (element: HTMLElement, options: {
        videoId: string;
        width: string;
        height: string;
        playerVars: Record<string, string | number>;
        events: { onError: (event: { data: number }) => void };
    }) => YouTubePlayer;
}

declare global {
    interface Window {
        YT?: YouTubePlayerApi;
        onYouTubeIframeAPIReady?: () => void;
    }
}

let apiPromise: Promise<YouTubePlayerApi> | null = null;

export function loadYouTubePlayerApi(): Promise<YouTubePlayerApi> {
    if (window.YT?.Player)
        return Promise.resolve(window.YT);
    if (apiPromise)
        return apiPromise;

    apiPromise = new Promise<YouTubePlayerApi>((resolve, reject) => {
        const previous = window.onYouTubeIframeAPIReady;
        const timeout = window.setTimeout(() => reject(new Error('YouTube player did not load.')), 15000);
        window.onYouTubeIframeAPIReady = () => {
            previous?.();
            window.clearTimeout(timeout);
            if (window.YT?.Player)
                resolve(window.YT);
            else
                reject(new Error('YouTube player is unavailable.'));
        };
        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            script.async = true;
            script.onerror = () => {
                window.clearTimeout(timeout);
                reject(new Error('YouTube player script failed.'));
            };
            document.head.appendChild(script);
        }
    }).catch(error => {
        apiPromise = null;
        throw error;
    });
    return apiPromise;
}

export function embedUrl(videoId: string): string {
    const url = new URL(`https://www.youtube.com/embed/${videoId}`);
    url.searchParams.set('autoplay', '1');
    url.searchParams.set('mute', '1');
    url.searchParams.set('controls', '1');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('enablejsapi', '1');
    url.searchParams.set('origin', window.location.origin);
    return url.toString();
}

export function embedVideoId(url: string): string | null {
    try {
        const parsed = new URL(url);
        if (!['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com'].includes(parsed.hostname))
            return null;
        const match = parsed.pathname.match(/^\/embed\/([\w-]{11})$/);
        return match?.[1] || null;
    } catch {
        return null;
    }
}
